import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import DateRangeSelect from '../common/DateRangeSelect/DateRangeSelect';
import { getCompanyNameSuggestions } from '../../services/autocompleteService';
import historyService from '../../services/historyLocalStorageService';
import trackingUtils from '../../utils/trackingUtils';
import Autocomplete from '../common/Autocomplete/Autocomplete';
import { isUnauthorized } from '../../services/utils/responseValidator';
import trackingConstants from '../../utils/trackingConstants';
import ButtonSubmit from '../common/ButtonSubmit/ButtonSubmit';
import ButtonClear from '../common/ButtonClear/ButtonClear';
import ButtonSwitch from '../common/ButtonSwitch/ButtonSwitch';
import translationPropType from '../../proptypes/translationPropType';
import {
    setFormField, setUseOrderPeriod, getCompanyReportStatsForOrder, getCompanyReportStats, clearAll,
} from '../../reduxStore/config/companyreport';
import { logout } from '../../reduxStore/config/user';

const styles = theme => ({
    forcedLabelBackground: {
        background: theme.palette.background.paper,
        padding: '0 2px',
    },
});

const CompanyReportForm = ({
    classes,
    searchActive,
    translation,
}) => {
    const dispatch = useDispatch();

    const {
        formState,
        useOrderPeriod,
    } = useSelector(state => state.companyreport);

    const {
        user,
        preferedLanguage,
    } = useSelector(state => state.user);

    const isAdvancedUser = user ? user.isAdvancedUser : false;

    const setField = (field, value) => {
        dispatch(setFormField({ [field]: value }));
    };

    const handleChangeEvent = (event) => {
        setField(event.target.name, event.target.value);
    };

    const handleAutocompleteEvent = field => (selected) => {
        setField(field, selected);
    };

    const handleUseOrderPeriod = () => {
        dispatch(setUseOrderPeriod());
    };

    const isValidForm = () => (
        formState[formState.searchType].length >= 3
    );

    const handleLogOut = (message) => {
        dispatch(logout(message));
    };

    const submit = (event) => {
        event.preventDefault();
        if (isValidForm()) {
            trackingUtils.trackEvent({
                category: trackingConstants.COMPANY_REPORT_SUMBIT,
                action: trackingConstants.COMPANY_REPORT_SEARCH_TYPES[formState.searchType],
                label: formState[[formState.searchType]],
            });

            if (formState.searchType === 'ORDER_NUMBER') {
                dispatch(getCompanyReportStatsForOrder(formState));
            } else {
                dispatch(getCompanyReportStats(formState));
            }
        }
    };

    const handleClear = () => {
        dispatch(clearAll());
    };

    const handleGetSuggestions = serviceMethod => query => serviceMethod(query)
        .catch(err => isUnauthorized(err.response) && handleLogOut(err.message));

    const suggestFromHistory = getHistory => query => new Promise(
        resolve => resolve(getHistory().filter(item => item.includes(query))),
    );

    const getHistoryMap = {
        COMPANY_NAME: historyService.getCompanyNameHistory,
        ORG_NUMBER: historyService.getOrgNoHistory,
        HID: historyService.getHidHistory,
        ORDER_NUMBER: historyService.getOrderHistory,
    };

    const getSuggestionsMap = {
        COMPANY_NAME: handleGetSuggestions(getCompanyNameSuggestions),
        ORG_NUMBER: suggestFromHistory(getHistoryMap.ORG_NUMBER),
        HID: suggestFromHistory(getHistoryMap.HID),
        ORDER_NUMBER: suggestFromHistory(getHistoryMap.ORDER_NUMBER),
    };

    return (
        <form onSubmit={submit} autoComplete="off">
            <Grid container spacing={2}>
                <Grid item xs={12} sm={5}>
                    <FormControl
                        margin="dense"
                        variant="outlined"
                        fullWidth
                    >
                        <InputLabel
                            htmlFor="searchType"
                            shrink
                            className={classes.forcedLabelBackground}
                        >
                            {translation.SEARCH_BY}
                        </InputLabel>
                        <Select
                            value={formState.searchType}
                            onChange={handleChangeEvent}
                            fullWidth
                            margin="dense"
                            input={
                                (
                                    <OutlinedInput
                                        labelWidth={0}
                                        notched
                                        name="searchType"
                                        id="searchType"
                                    />
                                )
                            }
                        >
                            <MenuItem value="HID">{translation.HID}</MenuItem>
                            <MenuItem value="ORG_NUMBER">{translation.ORG_NUMBER}</MenuItem>
                            <MenuItem value="COMPANY_NAME">{translation.COMPANY_NAME}</MenuItem>
                            <MenuItem value="ORDER_NUMBER">{translation.ORDER_NUMBER}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={7}>
                    <Autocomplete
                        value={formState[formState.searchType]}
                        handleChange={handleAutocompleteEvent(formState.searchType)}
                        getSuggestions={getSuggestionsMap[formState.searchType]}
                        getHistory={getHistoryMap[formState.searchType]}
                        inputName={formState.searchType}
                        label={translation[formState.searchType]}
                        placeholder={
                            translation.COMPANY_SEARCH_PLACEHOLDER[formState.searchType]
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    {formState.searchType === 'ORDER_NUMBER' && (
                        <ButtonSwitch
                            checked={useOrderPeriod}
                            label={translation.USE_ORDER_PERIOD}
                            name="useOrderPeriod"
                            onChange={handleUseOrderPeriod}
                        />
                    )}
                    {(formState.searchType !== 'ORDER_NUMBER' || (formState.searchType === 'ORDER_NUMBER' && !useOrderPeriod)) && (
                        <DateRangeSelect
                            to={formState.to}
                            locale={preferedLanguage}
                            from={formState.from}
                            setValue={setField}
                            translation={translation}
                            disabled={!isAdvancedUser}
                        />
                    )}
                </Grid>
                <Grid item xs={12}>
                    <ButtonSubmit
                        disabled={!isValidForm()}
                        title={translation.SEARCH}
                    />
                    <ButtonClear
                        disabled={!searchActive}
                        onClick={handleClear}
                        title={translation.CLEAR}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

CompanyReportForm.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    searchActive: PropTypes.bool.isRequired,
};

export default withStyles(styles)(CompanyReportForm);
